import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import AvatarChip from '../../../../../shared/avatar-chip.tsx';
import { DecomModel } from '../../../../../../types.ts';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete.tsx';
import DecomOrgAutoComplete from '../../decom-policy-grid-actions/decom-org-autocomplete.tsx';
import { DecomPolicyFormValues } from '../decom-policy-form-layout-old.tsx';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

interface BasicInfoStepOldProps {
  control: Control<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  trigger: UseFormTrigger<DecomPolicyFormValues>;
  decomModels: DecomModel[];
}

export default function BasicInfoStepOld({
  control,
  watch,
  setValue,
  errors,
  trigger,
  clearErrors,
  decomModels,
}: BasicInfoStepOldProps) {
  const autocompleteKeyOrgs = useRef(new Date().getTime().toString());
  const [autoCompleteResponseOrgs, setAutoCompleteResponseOrgs] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const orgs = watch('org') || [];
  const notificationUser = watch('notificationUser') || '';
  const triggerType = watch('triggerType') || 'suspension';
  const selectedActions =
    Object.values(watch('actions') ?? {})
      .flatMap(
        (day: { actions?: Array<{ actionType: string }> }) => day.actions ?? []
      )
      .map((a: { actionType: string }) => a.actionType) ?? [];

  const actionsData = watch('actions') || [];

  const policyTypeOptions = [
    { displayValue: 'User Suspension', value: 'suspension' },
    { displayValue: 'Org Unit Change', value: 'move' },
  ];

  function removeAction(actionType: string) {
    const updatedActions = Object.fromEntries(
      Object.entries(actionsData).map(([day, dayData]) => {
        const filteredActions = dayData.actions.filter(
          (a) => a.actionType !== actionType
        );
        return [
          day,
          {
            ...dayData,
            actions: filteredActions,
          },
        ];
      })
    );

    setValue('actions', updatedActions, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  return (
    <Stack spacing={4} width="100%">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
        <Stack spacing={0.9} sx={{ flex: 0.85, pr: 3 }}>
          <Stack>
            {/* Policy Name */}
            <Controller
              name="name"
              rules={{ required: 'Policy Name is required.' }}
              control={control}
              render={({ field }) => (
                <TextField
                  label="Policy Name"
                  required
                  fullWidth
                  {...field}
                  onBlur={(e) => {
                    field.onChange(e.target.value.trim());
                    field.onBlur();
                    if (field.value !== '') clearErrors('name');
                  }}
                  error={!!errors.name}
                  helperText={errors.name?.message ?? ' '}
                  autoComplete="off"
                />
              )}
            />
          </Stack>

          {/* Trigger Type + Act Immediately */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Controller
              name="triggerType"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label="Trigger Type"
                  fullWidth
                  value={field.value || ''}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                    setValue('validateUserIsStillSuspended', false, {
                      shouldDirty: true,
                    });
                  }}
                  required
                  sx={{ flex: 1 }}
                >
                  {policyTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Stack>
            <Controller
              name="actImmediately"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Act Immediately"
                  />
                  <FormHelperText sx={{ mt: 0, ml: 4 }}>
                    The decommissioning process is kicked off immediately based
                    on the trigger type, rather than being evaluated on a
                    nightly basis.
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
          {/* Select Org */}
          <Stack>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1">
                Select Organizations to apply this decom policy to:
              </Typography>
            </Box>
            <Stack flexDirection="row" gap={1}>
              <Box sx={{ width: '100%' }}>
                <Controller
                  name={'org'}
                  rules={{
                    required: 'Please provide at least one org unit.',
                  }}
                  control={control}
                  render={(field) => (
                    <DecomOrgAutoComplete
                      {...field}
                      newKey={autocompleteKeyOrgs.current}
                      autoCompleteValue={autoCompleteResponseOrgs}
                      setAutoCompleteValue={(newValue) => {
                        if (!newValue) return;

                        const existing = orgs || [];
                        const alreadyExists = existing.some(
                          (org) => org.toLowerCase() === newValue.toLowerCase()
                        );

                        if (!alreadyExists) {
                          const updated = [...existing, newValue];
                          setValue('org', updated, { shouldDirty: true });
                          trigger('org');
                        }

                        setAutoCompleteResponseOrgs('');
                        autocompleteKeyOrgs.current = new Date()
                          .getTime()
                          .toString();
                      }}
                      hasError={!!errors.org}
                      helperText={errors.org?.message ?? undefined}
                      decomModels={decomModels}
                    />
                  )}
                />
              </Box>
            </Stack>

            {orgs.length > 0 && (
              <Stack flexDirection="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {orgs.map((org) => (
                  <AvatarChip
                    key={org}
                    label={org}
                    type={'OrgUnit'}
                    email={org}
                    onDelete={() => {
                      setValue(
                        'org',
                        orgs.filter((orgUnit) => orgUnit !== org),
                        { shouldDirty: true }
                      );
                      trigger('org');
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          {/* More Configuration Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="text"
              size="small"
              sx={{ textTransform: 'none', textDecoration: 'none', mt: 2 }}
              onClick={() => setShowAdvanced((prev) => !prev)}
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Configuration
            </Button>
          </Box>

          <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {/* Summary Email */}
              <Stack sx={{ mb: 5 }}>
                <Typography variant="subtitle2">Summary Email</Typography>
                <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                  {notificationUser ? (
                    <AvatarChip
                      type="Unknown"
                      email={notificationUser}
                      onDelete={() => {
                        setValue('notificationUser', '', { shouldDirty: true });
                        setValue('sendSummary', false, { shouldDirty: true });
                      }}
                      size="small"
                    />
                  ) : (
                    <Controller
                      name="notificationUser"
                      control={control}
                      render={() => (
                        <Box sx={{ width: '100%' }}>
                          <SharedAutocomplete
                            sources={['users', 'groups']}
                            autocompleteValue={notificationUser}
                            setAutoCompleteValue={(newVal) => {
                              setValue('notificationUser', newVal, {
                                shouldDirty: true,
                              });
                              setValue('sendSummary', true, {
                                shouldDirty: true,
                              });
                              trigger('notificationUser');
                            }}
                            helperText="This email will show all actions to be taken for the day's decommissioning run. It will be sent at the start of each day's run."
                          />
                        </Box>
                      )}
                    />
                  )}
                </Stack>
              </Stack>

              {/* Leave Unsuspended */}
              <Stack spacing={0}>
                <Controller
                  name="keepUnsuspended"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Leave Unsuspended"
                      />
                      <FormHelperText sx={{ pl: 2, mt: 0 }}>
                        If no decommissioning action changed the user status
                        (e.g., Suspend User, Unsuspend User, Archive User), then
                        at the end of each decommissioning run, the user will be
                        set to Unsuspended.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Stack>

              {/* Pause On User Org Change */}
              <Stack spacing={0}>
                <Controller
                  name="validateUserIsStillInDecomOrg"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              if (selectedActions.includes('UserOrgChange')) {
                                //removing UserOrgChange
                                removeAction('UserOrgChange');
                              }
                            }}
                          />
                        }
                        label="Pause Decom On User Org Change"
                      />
                      <FormHelperText sx={{ pl: 2, mt: 0 }}>
                        Selecting this option will remove the User Org Change
                        action from this policy if it has been selected.
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Stack>
              {/* Pause On Unsuspension (if triggerType is 'suspension') */}
              {triggerType === 'suspension' && (
                <Stack spacing={0}>
                  <Controller
                    name="validateUserIsStillSuspended"
                    control={control}
                    render={({ field }) => (
                      <FormControl>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                if (selectedActions.includes('unsuspendUser')) {
                                  removeAction('unsuspendUser');
                                }
                              }}
                            />
                          }
                          label="Pause Decom On Unsuspension"
                        />
                        <FormHelperText sx={{ pl: 2, mt: 0 }}>
                          Selecting this option will remove the Unsuspended User
                          action from this policy if it has been selected.
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Stack>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </Stack>
    </Stack>
  );
}
